/**
 * Campo somente leitura com botao "Copiar" (URL de webhook, instancia...).
 *
 * `navigator.clipboard` so existe em contexto seguro (https ou localhost) e
 * pode ser negado pelo navegador. Quando falha, o campo recebe foco com o
 * texto selecionado e a dica muda para "Ctrl+C": a pessoa ainda consegue
 * copiar, e nada fica parecendo que funcionou quando nao funcionou.
 */

import { useEffect, useState } from 'react';
import { Badge, Button, Field, Input } from '../../../components/ui';

const FEEDBACK_MS = 2_400;

type Estado = 'parado' | 'copiado' | 'manual';

export function CampoCopiavel({
  label,
  value,
  hint,
}: {
  readonly label: string;
  readonly value: string;
  readonly hint?: string;
}): JSX.Element {
  const [estado, setEstado] = useState<Estado>('parado');

  useEffect(() => {
    if (estado === 'parado') {
      return undefined;
    }
    const timer = window.setTimeout(() => setEstado('parado'), FEEDBACK_MS);
    return () => window.clearTimeout(timer);
  }, [estado]);

  const copiar = async (inputId: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setEstado('copiado');
    } catch {
      const campo = document.getElementById(inputId);
      if (campo instanceof HTMLInputElement) {
        campo.focus();
        campo.select();
      }
      setEstado('manual');
    }
  };

  return (
    <Field label={label} hint={hint}>
      {({ id, describedBy }) => (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Input
            id={id}
            aria-describedby={describedBy}
            readOnly
            value={value}
            onFocus={(event) => event.currentTarget.select()}
            className="font-mono text-xs"
          />
          <div className="flex shrink-0 items-center gap-2">
            <Button variant="secondary" icon="copiar" onClick={() => void copiar(id)}>
              Copiar
            </Button>
            <span role="status" className="min-w-0">
              {estado === 'copiado' ? (
                <Badge tone="good" icon="check">
                  Copiado
                </Badge>
              ) : null}
              {estado === 'manual' ? (
                <Badge tone="warning" dot>
                  Selecionado: use Ctrl+C
                </Badge>
              ) : null}
            </span>
          </div>
        </div>
      )}
    </Field>
  );
}
